import { Button, Group } from '@mantine/core';
import { IconBook, IconPlus } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { CreateBookletModal } from 'src/Components/Modals/CreateBookletModal';
import { QRCodeModal } from 'src/Components/Modals/QRCodeModal';
import { PracticalInformationFilters } from 'src/Components/PracticalInformation/PracticalInformationFilters';
import { PracticalInformationSuggestions } from 'src/Components/PracticalInformation/PracticalInformationSuggestions';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import useFetchAllPracticalInformation from 'src/Hooks/Queries/useFetchAllPracticalInformation';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import usePracticalInformationFilters from 'src/Hooks/Shared/usePracticalInformationFilters';
import { trackEvent } from 'src/Services/Matomo';
import { trackPracticalInformationFilterApplied } from 'src/Services/Mixpanel/events';

import PracticalInformationList from '../../../Components/PracticalInformation/PracticalInformationList/PracticalInformationList.component';

import styles from './PracticalnformationMainPage.module.scss';

const PracticalnformationMainPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('practicalInformation');
  const { hasPermission: canCreate } = useCanCreate('practicalInformation');
  const { hasPermission: canExport } = useCanCreate('entityExport');

  const assetAboveQuotas = useIsAssetAboveQuotas();
  const { selectedCategories, setSelectedCategories } = usePracticalInformationFilters();
  const { data: allInformation } = useFetchAllPracticalInformation();
  const hasTotalItems = (allInformation?.length ?? 0) > 0;

  const [bookletModalOpened, setBookletModalOpened] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);

  const handleShowQrCode = (url: string) => {
    setBookletModalOpened(false);
    setQrCodeUrl(url);
  };

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  const handleFiltersChange = (next: string[]) => {
    setSelectedCategories(next);
    // On évite de tracker quand on revient à "Toutes" (= aucune cat. sélectionnée).
    if (next.length > 0) {
      trackEvent('PracticalInformation', 'filter', next.join(','));
      trackPracticalInformationFilterApplied({ categories: next, count: next.length });
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.topLine}>
        <div className={styles.titleColumn}>
          <div className={styles.title}>{t('practicalInformation.title', 'Infos pratiques')}</div>
          <div className={styles.subtitle}>
            {t(
              'practicalInformation.subtitle',
              'Retrouvez ici toutes les informations utiles pour la gestion commune du bien'
            )}
          </div>
        </div>
        <Group>
          <VideoTutorialButton videoUrl={'https://youtu.be/pR9WxW5YM2E'} />
          {canExport && hasTotalItems && (
            <Button
              variant="outline"
              onClick={() => setBookletModalOpened(true)}
              leftSection={<IconBook size={20} />}
            >
              {isMobile
                ? t('practicalInformation.createBookletShort', 'Livret')
                : t('practicalInformation.createBooklet', 'Créer un livret')}
            </Button>
          )}
          {canCreate && (
            <Button
              onClick={() => navigate('new')}
              leftSection={<IconPlus size={20} />}
              disabled={assetAboveQuotas}
            >
              {isMobile
                ? t('practicalInformation.addPracticalInformationShort', 'Ajouter une info')
                : t('practicalInformation.addPracticalInformation', 'Ajouter une info pratique')}
            </Button>
          )}
        </Group>
      </div>
      {hasTotalItems && (
        <PracticalInformationFilters
          selectedCategories={selectedCategories}
          onChange={handleFiltersChange}
        />
      )}
      {canCreate && <PracticalInformationSuggestions />}
      <PracticalInformationList
        selectedCategories={selectedCategories}
        onResetFilters={() => setSelectedCategories([])}
      />
      <CreateBookletModal
        opened={bookletModalOpened}
        onClose={() => setBookletModalOpened(false)}
        onShowQrCode={handleShowQrCode}
      />
      <QRCodeModal
        open={!!qrCodeUrl}
        url={qrCodeUrl ?? ''}
        onClose={() => setQrCodeUrl(undefined)}
        exportName={t('booklet.qrCodeTitle', 'Livret infos pratiques')}
      />
      <Outlet />
    </div>
  );
};

export default PracticalnformationMainPage;
